import { SharedApplicationConstants } from '@ddt-web/domain';
import * as get from 'lodash/get';
import * as groupBy from 'lodash/groupBy';
import * as isEmpty from 'lodash/isEmpty';

import { MODEL_TRADE_FREQUENCY_ORDER } from '../constants';

export class ModelsTransformUtil {

	public static transformModelPortfolios(configs: Array<any>, ids: Array<string>, filterIntoFamilies: boolean = false): any {
		let models = [];
		if (configs && configs.length && ids && ids.length) {
			configs.sort((a, b) => ids.indexOf(a.id.toString()) - ids.indexOf(b.id.toString()));
			const grouped = groupBy(configs, 'familyCode');
			models = Object.entries(grouped).map(([key, value]) => {
				return {
					familyCode: key,
					familyName: get(value, '[0].familyName'),
					isDefaultFamilyForVehiclePref: get(value, '[0].isDefaultFamilyForVehiclePref'),
					subType: get(value, '[0].subType'),
					groups: get(value, '[0].groups'),
					portfolios: value,
					displayOrder: get(value, '[0].displayOrder', ''),
					modelExternalName: get(value, '[0].modelExternalFamilyName'),
					enableCmsRenaming: get(value, '[0].enableCmsRenaming')
				};
			});
		}

		if (filterIntoFamilies) {
			return {
				defaultFamilies: models.filter(family => family.isDefaultFamilyForVehiclePref),
				additionalFamilies: models.filter(family => !family.isDefaultFamilyForVehiclePref)
			};
		} else {
			return models;
		}
	}

	public static sortModelPortfolios(models: Array<any>, comparatorKey: string): Array<any> {
		if (!isEmpty(models)) {
			const lastIndex = models.length;
			return models.sort((a, b) => {
				const valueA = get(a, comparatorKey) ? Number(get(a, comparatorKey)) : lastIndex;
				const valueB = get(b, comparatorKey) ? Number(get(b, comparatorKey)) : lastIndex;
				return valueA >= valueB ? 1 : -1;
			});
		}
		return [];
	}

	/**
	  * TODO: Waris- this will be deprecated after meta data migration, use sortListWithKeys
	  */
	/**
	 * Method use to sort the array in ascending order when asc is true and descending order when asc is false on basis of primaryComparatorKey
	 * When primaryComparatorKey is equal when the adjacent elements are sorted in ascending order on basis of secondaryComparatorKey
	 * @param models 
	 * @param primaryComparatorKey 
	 * @param secondaryComparatorKey 
	 * @param asc 
	 * @returns 
	 */
	public static sortModelPortfoliosWithKeys(models: Array<any>, primaryComparatorKey: string, secondaryComparatorKey: string, asc: boolean): Array<any> {
		if (!isEmpty(models)) {
			const lastIndex = models.length;
			return models.sort((a, b) => {
				const primaryValueA = get(a, primaryComparatorKey) ? new Date(get(a, primaryComparatorKey)).getTime() : lastIndex;
				const primaryValueB = get(b, primaryComparatorKey) ? new Date(get(b, primaryComparatorKey)).getTime() : lastIndex;
				const secondaryValueA = get(a, secondaryComparatorKey) ? Number(get(a, secondaryComparatorKey)) : lastIndex;
				const secondaryValueB = get(b, secondaryComparatorKey) ? Number(get(b, secondaryComparatorKey)) : lastIndex;
				if (primaryValueA === primaryValueB) {
					return secondaryValueA >= secondaryValueB ? 1 : -1;
				}
				if (asc) {
					return primaryValueA >= primaryValueB ? 1 : -1;
				} else {
					return primaryValueA >= primaryValueB ? -1 : 1;
				}
			});
		}
		return [];
	}

	/**
	 * Method use to sort the models in ascending order based on trade frequency by default.
	 * BLK models should be sorted by display order and CMS & research models should follow the trade frequency logic.
	 * It should sort the models based on trade date when sortByTradeDate is set to true.
	 * 
	 * @param families 
	 * @param category 
	 * @param familySortingParams 
	 * @param sortByTradeDate 
	 * @returns 
	 */
	public static sortFamiliesWithTradeDateOrFrequency(families: Array<any>, category: string, familySortingParams: { [key: string]: string }, sortByTradeDate: boolean): Array<any> {
		if (sortByTradeDate) {
			return ModelsTransformUtil.sortModelPortfoliosWithKeys(families, familySortingParams.asOfDate, familySortingParams.displayOrder, false);
		} else {
			return (category === SharedApplicationConstants.MODEL_FAMILY.BLACKROCK_FAMILY_GROUP)
				? ModelsTransformUtil.sortModelPortfolios(families, familySortingParams.displayOrder)
				: ModelsTransformUtil.sortListWithKeys(
					families,
					familySortingParams.subType,
					familySortingParams.tradeFrequency,
					true,
					['CUSTOM', 'BLK'],
					MODEL_TRADE_FREQUENCY_ORDER
				);
		}
	}

	/**
	 * Method use to sort the array in ascending order when asc is true and descending order when asc is false on basis of primaryComparatorKey
	 * When primaryComparatorKey is equal when the adjacent elements are sorted in ascending order on basis of secondaryComparatorKey
	 * @param models 
	 * @param primaryComparatorKey 
	 * @param secondaryComparatorKey 
	 * @param asc 
	 * @returns 
	 */
	public static sortListWithKeys(
		models: Array<any>,
		primaryComparatorKey: string,
		secondaryComparatorKey: string,
		asc: boolean,
		primaryComparatorKeyOrder?: string[],
		secondaryComparatorKeyOrder?: string[]
	): Array<any> {
		if (!isEmpty(models)) {
			const lastIndex = models.length;
			return models.sort((a, b) => {
				const primaryValueA = get(a, primaryComparatorKey) ? this.getComparatorValue(get(a, primaryComparatorKey), primaryComparatorKeyOrder) : lastIndex;
				const primaryValueB = get(b, primaryComparatorKey) ? this.getComparatorValue(get(b, primaryComparatorKey), primaryComparatorKeyOrder) : lastIndex;
				const secondaryValueA = get(a, secondaryComparatorKey) ? this.getComparatorValue(get(a, secondaryComparatorKey), secondaryComparatorKeyOrder) : lastIndex;
				const secondaryValueB = get(b, secondaryComparatorKey) ? this.getComparatorValue(get(b, secondaryComparatorKey), secondaryComparatorKeyOrder) : lastIndex;
				if (primaryValueA === primaryValueB) {
					return secondaryValueA >= secondaryValueB ? 1 : -1;
				}
				if (asc) {
					return primaryValueA >= primaryValueB ? 1 : -1;
				} else {
					return primaryValueA >= primaryValueB ? -1 : 1;
				}
			});
		}
		return [];
	}

	private static getComparatorValue(value: any, order: string[]): number | string {
		// Date or string
		if (isNaN(value)) {
			if (new Date(value) instanceof Date && !isNaN(new Date(value).valueOf())) {
				return new Date(value).getTime();
			} else {
				return order.indexOf(String(value));
			}
		} else {
			return Number(value);
		}
	}
}
